import styled from "@emotion/styled/macro";
import {
  MOBILE_BREAKPOINT,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { useState } from "react";
import {
  ReviewFinancialSummary,
  ReviewOffer,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { HorizontalLine } from "components/atoms/HorizontalLine";
import { faqArray, newHomesFaqArray, type FAQEntry } from "fixtures/faqArray";

type FAQProps = {
  offer: ReviewOffer;
  isNewHomes?: boolean;
};

type FAQItemProps = Pick<FAQEntry, "question" | "answer">;

const faqToShow = (summary: ReviewFinancialSummary): FAQEntry[] => {
  const type = summary.type.toLowerCase();
  const pkg = summary.servicePackage.toLowerCase();

  return faqArray.filter((entry) => !entry.show || entry.show(type, pkg));
};

const FAQItem = ({ question, answer }: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <FAQItemContainer isOpen={isOpen}>
      <FAQItemQuestion>
        <Question
          size={18}
          fontWeight={"medium"}
          color={tokens.BRAND_HERO_BLUE}
        >
          {question}
        </Question>
        <FAQItemToggle
          size={24}
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        >
          <SvgIcon name={SvgNames.ChevronDown} color={tokens.BRAND_HERO_BLUE} />
        </FAQItemToggle>
      </FAQItemQuestion>
      <AnimatedPanel isOpen={isOpen}>
        <FAQItemAnswer
          size={16}
          fontWeight="regular"
          color={tokens.BRAND_HERO_BLUE}
        >
          {answer}
        </FAQItemAnswer>
      </AnimatedPanel>
    </FAQItemContainer>
  );
};

const FAQ = ({ offer, isNewHomes }: FAQProps) => {
  const [faqOpen, setFaqOpen] = useState(false);
  const faqItems = isNewHomes
    ? newHomesFaqArray
    : faqToShow(offer.financial.summary);

  return (
    <FAQContainer>
      <FAQHeader>
        <FAQTitle
          color={tokens.BRAND_HERO_BLUE}
          size={44}
          fontWeight={"regular"}
        >
          FAQ
        </FAQTitle>
        <FAQHeaderToggle
          size={40}
          type="button"
          aria-expanded={faqOpen}
          onClick={() => setFaqOpen(!faqOpen)}
        >
          <PlusMinusBarHorizontal aria-hidden />
          <PlusMinusBarVertical isOpen={faqOpen} aria-hidden />
        </FAQHeaderToggle>
      </FAQHeader>
      <AnimatedPanel isOpen={faqOpen}>
        <FAQItemList>
          {faqItems.map((elem) => (
            <FAQItem
              key={elem.question}
              question={elem.question}
              answer={elem.answer}
            />
          ))}
        </FAQItemList>
      </AnimatedPanel>
      {!faqOpen && <FAQDivider />}
    </FAQContainer>
  );
};

const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1005px;
  padding: 96px 64px 0px;
  margin: 0px auto 64px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 96px 16px 0px;
  }
`;

const FAQHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const FAQTitle = styled(Typography)`
  letter-spacing: -0.88px;
  && {
    line-height: 120%;
  }
`;

const FAQDivider = styled(HorizontalLine)`
  margin: 30px 0px 0px;
  border-color: ${tokens.TINTS_OFF_WHITE_30};
`;

const FAQToggle = styled.button<{ size: number }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0px;
  margin: 0px;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
`;

const FAQHeaderToggle = styled(FAQToggle)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${tokens.BRAND_HERO_BLUE};
  border-radius: 50%;
`;

const PlusMinusBarBase = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  background: ${tokens.WHITE};
  border-radius: 2px;
`;

const PlusMinusBarHorizontal = styled(PlusMinusBarBase)`
  width: 20px;
  height: 2.5px;
  transform: translate(-50%, -50%);
`;

const PlusMinusBarVertical = styled(PlusMinusBarBase)<{ isOpen: boolean }>`
  width: 2.5px;
  height: 20px;
  transform: translate(-50%, -50%) scaleY(${({ isOpen }) => (isOpen ? 0 : 1)});
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center center;
`;

const FAQItemToggle = styled(FAQToggle)<{ isOpen: boolean }>`
  svg {
    transition: transform 300ms ease;
    transform: rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  }
`;

const AnimatedPanel = styled.div<{ isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${({ isOpen }) => (isOpen ? "1fr" : "0fr")};
  transition: grid-template-rows 300ms ease;

  > * {
    min-height: 0;
    overflow: hidden;
  }
`;

const FAQItemContainer = styled.li<{ isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 24px 0px;
  gap: ${({ isOpen }) => (isOpen ? "16px" : "0px")};
  transition: gap 300ms ease;
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_30};
`;

const FAQItemQuestion = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-right: 8px;
`;

const FAQItemAnswer = styled(Typography)`
  letter-spacing: -0.32px;
  && {
    line-height: 140%;
  }
`;

const Question = styled(Typography)`
  letter-spacing: -0.36px;
  && {
    line-height: 120%;
  }
`;

const FAQItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  & > li:last-child {
    border-bottom: none;
  }
`;

export { FAQ };
